// src/store/slices/wishlistSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as wishlistApi from '../api/wishlist';

// Async thunks
export const fetchWishlist = createAsyncThunk(
  'wishlist/fetchWishlist',
  async (_, { rejectWithValue }) => {
    try {
      const response = await wishlistApi.getWishlist();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch wishlist');
    }
  }
);

export const addToWishlist = createAsyncThunk(
  'wishlist/addToWishlist',
  async (productId, { rejectWithValue }) => {
    try {
      const response = await wishlistApi.addToWishlist(productId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to add item to wishlist');
    }
  }
);

export const removeFromWishlist = createAsyncThunk(
  'wishlist/removeFromWishlist',
  async (productId, { rejectWithValue }) => {
    try {
      const response = await wishlistApi.removeFromWishlist(productId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to remove item from wishlist');
    }
  }
);

export const clearWishlist = createAsyncThunk(
  'wishlist/clearWishlist',
  async (_, { rejectWithValue }) => {
    try {
      const response = await wishlistApi.clearWishlist();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to clear wishlist');
    }
  }
);

export const moveToCart = createAsyncThunk(
  'wishlist/moveToCart',
  async (productId, { rejectWithValue }) => {
    try {
      const response = await wishlistApi.moveToCart(productId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to move item to cart');
    }
  }
);

export const checkWishlistItem = createAsyncThunk(
  'wishlist/checkWishlistItem',
  async (productId, { rejectWithValue }) => {
    try {
      const response = await wishlistApi.checkWishlistItem(productId);
      return { productId, ...response };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to check wishlist status');
    }
  }
);

// Initial state
const initialState = {
  wishlist: { 
    userId: null,
    items: [] 
  },
  itemsCount: 0,
  loading: {
    wishlist: false,
    add: false,
    remove: false,
    clear: false,
    moveToCart: false,
    check: false
  },
  error: null,
  wishlistStatus: {} // Track wishlist status by product ID
};

// Slice
const wishlistSlice = createSlice({
  name: 'wishlist',
  initialState,
  reducers: {
    resetWishlist: (state) => {
      state.wishlist = { userId: null, items: [] };
      state.itemsCount = 0;
      state.wishlistStatus = {};
      
      // Reset loading states
      Object.keys(state.loading).forEach(key => {
        state.loading[key] = false;
      });
      
      // Clear any errors
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    // Fetch wishlist
    builder
      .addCase(fetchWishlist.pending, (state) => {
        state.loading.wishlist = true;
        state.error = null;
      })
      .addCase(fetchWishlist.fulfilled, (state, action) => {
        state.wishlist = action.payload.data.wishlist;
        state.itemsCount = state.wishlist.items.length;
        state.loading.wishlist = false;
        
        // Update wishlist status for each item
        state.wishlist.items.forEach(item => {
          state.wishlistStatus[item.productId] = true;
        });
      })
      .addCase(fetchWishlist.rejected, (state, action) => {
        state.loading.wishlist = false;
        state.error = action.payload?.message || 'Failed to fetch wishlist';
      });
    
    // Add to wishlist
    builder
      .addCase(addToWishlist.pending, (state) => {
        state.loading.add = true;
        state.error = null;
      })
      .addCase(addToWishlist.fulfilled, (state, action) => {
        state.wishlist = action.payload.data.wishlist;
        state.itemsCount = state.wishlist.items.length;
        state.loading.add = false;
        
        // Update wishlist status
        const newItems = state.wishlist.items || [];
        newItems.forEach(item => {
          state.wishlistStatus[item.productId] = true;
        });
      })
      .addCase(addToWishlist.rejected, (state, action) => {
        state.loading.add = false;
        state.error = action.payload?.message || 'Failed to add item to wishlist';
      });
    
    // Remove from wishlist
    builder
      .addCase(removeFromWishlist.pending, (state) => {
        state.loading.remove = true;
        state.error = null;
      })
      .addCase(removeFromWishlist.fulfilled, (state, action) => {
        state.wishlist = action.payload.data.wishlist;
        state.itemsCount = state.wishlist.items.length;
        state.loading.remove = false;
        
        // Update wishlist status
        const productId = action.meta.arg;
        state.wishlistStatus[productId] = false;
      })
      .addCase(removeFromWishlist.rejected, (state, action) => {
        state.loading.remove = false;
        state.error = action.payload?.message || 'Failed to remove item from wishlist';
      });
    
    // Clear wishlist
    builder
      .addCase(clearWishlist.pending, (state) => {
        state.loading.clear = true;
        state.error = null;
      })
      .addCase(clearWishlist.fulfilled, (state, action) => {
        state.wishlist = action.payload.data.wishlist;
        state.itemsCount = 0;
        state.loading.clear = false;
        state.wishlistStatus = {};
      })
      .addCase(clearWishlist.rejected, (state, action) => {
        state.loading.clear = false;
        state.error = action.payload?.message || 'Failed to clear wishlist';
      });
    
    // Move to cart
    builder
      .addCase(moveToCart.pending, (state) => {
        state.loading.moveToCart = true;
        state.error = null;
      })
      .addCase(moveToCart.fulfilled, (state, action) => {
        state.wishlist = action.payload.data.wishlist;
        state.itemsCount = state.wishlist.items.length;
        state.loading.moveToCart = false;
        
        // Update wishlist status
        const productId = action.meta.arg;
        state.wishlistStatus[productId] = false;
      })
      .addCase(moveToCart.rejected, (state, action) => {
        state.loading.moveToCart = false;
        state.error = action.payload?.message || 'Failed to move item to cart';
      });
    
    // Check wishlist item
    builder
      .addCase(checkWishlistItem.pending, (state) => {
        state.loading.check = true;
        state.error = null;
      })
      .addCase(checkWishlistItem.fulfilled, (state, action) => {
        const { productId, data } = action.payload;
        state.wishlistStatus[productId] = data.isInWishlist;
        state.loading.check = false;
      })
      .addCase(checkWishlistItem.rejected, (state, action) => {
        state.loading.check = false;
        state.error = action.payload?.message || 'Failed to check wishlist status';
      });
      
    // Add this matcher to handle logout actions from auth slice
    builder.addMatcher(
      (action) => action.type === 'auth/logout' || action.type === 'auth/logoutUser/fulfilled',
      (state) => {
        // Reset wishlist on logout
        state.wishlist = { userId: null, items: [] };
        state.itemsCount = 0;
        state.wishlistStatus = {};
        
        // Reset loading states
        Object.keys(state.loading).forEach(key => {
          state.loading[key] = false;
        });
        
        // Clear any errors
        state.error = null;
      }
    );
  }
});

export const { resetWishlist } = wishlistSlice.actions;

export default wishlistSlice.reducer;